import { injectable } from 'inversify';
import { getI18n } from 'react-i18next';

import { configSchema } from '../utils/configSchema';
import { AppError } from '../utils/error';
import type { Config, Styling } from '../../types/config';
import env from '../env';
import { logError } from '../logger';
import { matchesFilter } from '../utils/matchesFilter';

/**
 * JWP supports most, but not all of our config properties
 */
type ConfigResponse = Omit<Config, 'styling'> & {
  styling?: Omit<Styling, 'highlightContrastColor'> | null;
};

/**
 * Set config setup changes in both config.service.ts and config.d.ts
 * */

@injectable()
export default class ConfigService {
  protected CONFIG_HOST = env.APP_CONFIG_API_BASE_URL;
  // Explicitly set default config here as a local variable,
  // otherwise if it's a module level const, the merge below causes changes to nested properties
  protected DEFAULT_CONFIG: Config = {
    id: '',
    siteName: '',
    description: '',
    siteId: '',
    assets: {
      banner: '/images/logo.png',
    },
    content: [],
    menu: [],
    integrations: {},
    styling: {
      footerText: '',
    },
    features: {},
  };

  protected getTranslatedProperty = <K extends keyof ConfigResponse>(config: ConfigResponse, property: K): ConfigResponse[K] => {
    const i18n = getI18n();
    const translatedProperty = config.custom?.[`${property}-${i18n.language}`];

    if (typeof translatedProperty === 'string') {
      return translatedProperty as ConfigResponse[K];
    }

    return config[property];
  };

  protected enrichConfig = (config: ConfigResponse): Config => {
    const { content, custom } = config;
    const updatedContent = content.map((content) => Object.assign({ featured: false }, content));

    // The JWP config does not support `styles.highlightContrastColor` so we take it from custom.
    const highlightContrastColor = typeof custom?.highlightContrastColor === 'string' ? custom.highlightContrastColor : undefined;

    return {
      ...config,
      siteName: this.getTranslatedProperty(config, 'siteName') || env.APP_NAME || 'My OTT Application',
      description:
        this.getTranslatedProperty(config, 'description') || env.APP_DESCRIPTION || 'JW OTT Webapp is an open-source, dynamically generated video website.',
      content: updatedContent,
      styling: {
        ...config.styling,
        highlightContrastColor,
      },
    };
  };

  getDefaultConfig = (): Config => {
    return this.DEFAULT_CONFIG;
  };

  validateConfig = async (config?: Config): Promise<Config> => {
    try {
      return (await configSchema.validate(config, {
        strict: true,
      })) as Config;
    } catch (error) {
      logError('ConfigService', 'config validation failed', { error });
      throw error;
    }
  };

  formatSourceLocation = (source?: string) => {
    if (!source) {
      return undefined;
    }

    if (source.match(/^[a-z,\d]{8}$/)) {
      return `${this.CONFIG_HOST}/apps/configs/${source}.json`;
    }

    return source;
  };

  loadConfig = async (configLocation: string | undefined) => {
    const i18n = getI18n();

    const errorPayload = {
      title: i18n.t('error:config_invalid'),
      description: i18n.t('error:check_your_config'),
      helpLink: 'https://github.com/jwplayer/ott-web-app/blob/develop/docs/configuration.md',
    };

    if (!configLocation) {
      throw new AppError('No config location found', errorPayload);
    }

    const response = await fetch(configLocation, {
      headers: {
        Accept: 'application/json',
      },
      method: 'GET',
    }).catch(() => {
      throw new AppError('Failed to load the config', errorPayload);
    });

    if (!response.ok) {
      throw new AppError('Failed to load the config', errorPayload);
    }

    const data = (await response.json()) as ConfigResponse;

    if (!data) {
      throw new Error('No config found');
    }

    return this.enrichConfig(data);
  };

  getContentItems = async ({ config, language }: { config: Config; language: string; isLoggedIn: boolean; hasSubscription: boolean }) => {
    let content = config.content;

    // optionally, we can add more filters here. For example when the user is logged in or has a subscription
    if (config.custom?.['contentItemsFilter'] === 'language') {
      content = content.filter((content) => matchesFilter(content.filterTags, language, true, 'language'));
    }

    return content;
  };

  getMenuItems = async ({ config, language }: { config: Config; language: string; isLoggedIn: boolean; hasSubscription: boolean }) => {
    let menu = config.menu;

    // optionally, we can add more filters here. For example when the user is logged in or has a subscription
    if (config.custom?.['menuItemsFilter'] === 'language') {
      menu = menu.filter((menuItem) => !menuItem.custom || matchesFilter(menuItem.custom['language'], language, true, 'language'));
    }

    return menu;
  };
}
